import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import DelegationBanner from '@shared/components/DelegationBanner'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import useQualities from '@shared/hooks/useQualities'
import useGetRecognition from '@tenant/recognition/useGetRecognition'
import { T } from '@transifex/react'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import BackModalHeader from '../BackModalHeader'
import RecognitionModalHeader from '../RecognitionModalHeader'
import RecognitionModalViewLayout from '../RecognitionModalViewLayout'
import useRecognitionModal, { RecognitionModalViews } from '../useRecognitionModal'
import DraftAndScheludedButton from './DraftAndScheduledButton'
import SendRecognitionForm from './SendRecognitionForm'
import FormSubmissionExceptions from './formSubmissionExceptions/FormSubmissionExceptions'
import ScheduledRecognitionSend from './scheduledRecognitionSend/ScheduledRecognitionSend'
import useSendRecognitionForm, { RecognitionFormData } from './useSendRecognitionForm'
import { useSendRecognitionFormStore } from './useSendRecognitionFormStore'

type SendRecognitionViewProps = {
  setShowAnimation: (showAnimation: boolean) => void
}

enum SendRecognitionViewState {
  Send,
  ScheduledSend,
  PointsException,
}
const SendRecognitionView: React.FC<SendRecognitionViewProps> = ({ setShowAnimation }) => {
  const { dialogData, setView } = useRecognitionModal()
  const { recognition } = useGetRecognition(
    ['sendToAndSender', 'attachmentUrls', 'qualities'],
    dialogData?.recognitionId,
  )
  const { qualities } = useQualities()
  const { setIsFormDirty } = useSendRecognitionFormStore()
  const [viewState, setViewState] = useState<SendRecognitionViewState>(
    SendRecognitionViewState.Send,
  )
  const {
    form,
    isFormDirty,
    isEditMode,
    pointsExceptions,
    setPointsExceptions,
    publishRecognition,
    handleSaveAsDraft,
    handleResetRecognitionForm,
    isPendingCreateOrSave,
  } = useSendRecognitionForm(setShowAnimation, !isEmpty(qualities), recognition)
  const { setValue, handleSubmit, trigger } = form
  const isDelegation = !!dialogData?.delegatorId

  const submitForm = async (data: RecognitionFormData) => {
    return publishRecognition(data, !!pointsExceptions, handleResetRecognitionForm)
  }

  const handleScheduleSend = async () => {
    const isValid = await trigger()
    if (isValid) {
      setViewState(SendRecognitionViewState.ScheduledSend)
    }
  }

  useEffect(() => {
    if (pointsExceptions) {
      setViewState(SendRecognitionViewState.PointsException)
    }
  }, [pointsExceptions])

  useEffect(() => {
    setIsFormDirty(isFormDirty)
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])

  useEffect(() => {
    if (!dialogData) return

    const { recipients, qualities } = dialogData

    if (!!recipients && recipients.length > 0) {
      setValue('sendTo', recipients, { shouldValidate: true })
    }

    if (!!qualities && qualities.length > 0) {
      setValue('qualities', qualities, { shouldValidate: true })
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogData])

  const renderHeader = () => {
    switch (viewState) {
      case SendRecognitionViewState.ScheduledSend:
        return (
          <BackModalHeader
            onBackClicked={() => {
              setValue('scheduledAt', recognition?.scheduledAt || null, { shouldValidate: true })
              setViewState(SendRecognitionViewState.Send)
            }}
            title={<T _str="Schedule send" />}
          />
        )
      case SendRecognitionViewState.PointsException:
        return (
          <BackModalHeader
            onBackClicked={() => {
              setPointsExceptions(undefined)
              setViewState(SendRecognitionViewState.Send)
            }}
            title={<T _str="Point exceptions" />}
          />
        )
      case SendRecognitionViewState.Send:
        return <RecognitionModalHeader />
    }
  }

  return (
    <>
      {renderHeader()}
      {isDelegation && (
        <Box>
          <DelegationBanner
            delegatorId={dialogData?.delegatorId}
            delegatorFirstName={dialogData?.delegatorFirstName}
            delegatorLastName={dialogData?.delegatorLastName}
            delegatorAvatarUrls={dialogData?.delegatorAvatarUrls}
          />
        </Box>
      )}
      <RecognitionModalViewLayout>
        <FormContextWrapper formMethods={form} onSubmit={submitForm}>
          {viewState === SendRecognitionViewState.Send && (
            <Stack>
              {!isDelegation && (
                <DraftAndScheludedButton
                  onClick={() =>
                    setView({
                      view: RecognitionModalViews.DraftAndInProgressRecognitions,
                    })
                  }
                />
              )}
              <SendRecognitionForm
                isEditMode={isEditMode}
                qualities={qualities}
                onSend={handleSubmit(submitForm)}
                onScheduleSend={handleScheduleSend}
                onSaveAsDraft={() => handleSaveAsDraft()}
                isSavingAsDraft={isPendingCreateOrSave}
                delegatorId={dialogData?.delegatorId}
              />
            </Stack>
          )}
          {viewState === SendRecognitionViewState.ScheduledSend && (
            <ScheduledRecognitionSend
              recognition={recognition}
              onScheduleClicked={handleSubmit(handleSaveAsDraft)}
            />
          )}
          {viewState === SendRecognitionViewState.PointsException && (
            <FormSubmissionExceptions
              pointsExceptions={pointsExceptions}
              delegatorId={dialogData?.delegatorId}
            />
          )}
        </FormContextWrapper>
      </RecognitionModalViewLayout>
    </>
  )
}

export default SendRecognitionView
